import { useState } from "react";
import { useJournalHistory } from "@/hooks/useJournalHistory";

interface PastProblemListPickerProps {
  ownerUid: string;
  centerId: number;
  studiomateMemberId: number | undefined;
  memberName?: string;
  onSelect: (text: string) => void;
}

function toDate(createdAt: unknown): Date | null {
  if (typeof createdAt === "string") {
    const d = new Date(createdAt);
    return isNaN(d.getTime()) ? null : d;
  }
  // Firestore Timestamp 객체 (serverTimestamp()로 저장된 데이터)
  if (createdAt && typeof createdAt === "object") {
    if ("toDate" in createdAt && typeof (createdAt as { toDate: unknown }).toDate === "function") {
      return (createdAt as { toDate(): Date }).toDate();
    }
    if ("seconds" in createdAt && typeof (createdAt as { seconds: unknown }).seconds === "number") {
      return new Date((createdAt as { seconds: number }).seconds * 1000);
    }
  }
  return null;
}

function formatDate(createdAt: unknown): string {
  try {
    const date = toDate(createdAt);
    if (!date) return "날짜 미상";
    const days = ["일", "월", "화", "수", "목", "금", "토"];
    const m = date.getMonth() + 1;
    const d = date.getDate();
    const dow = days[date.getDay()];
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    return `${m}/${d} (${dow}) ${hh}:${mm}`;
  } catch {
    return "날짜 미상";
  }
}

export function PastProblemListPicker({
  ownerUid,
  centerId,
  studiomateMemberId,
  memberName,
  onSelect,
}: PastProblemListPickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { data: journals, isLoading } = useJournalHistory(
    ownerUid,
    centerId,
    studiomateMemberId,
    memberName,
  );

  // 문제 목록이 있는 일지만 필터 (최신순, 최대 8개)
  const items = journals?.filter((j) => j.problemList?.trim()).slice(0, 8) ?? [];

  // 히스토리 없으면 렌더 안 함
  if (!isLoading && items.length === 0) return null;

  return (
    <div className="mt-1.5">
      <button
        type="button"
        className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span>이전 문제 목록</span>
        <span>{isOpen ? "▲" : "▼"}</span>
      </button>

      {isOpen && (
        <div className="mt-1.5 border border-gray-200 rounded-lg overflow-hidden">
          {isLoading ? (
            <p className="text-xs text-gray-400 px-3 py-2">불러오는 중...</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {items.map((journal) => (
                <li key={journal.id}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-primary-50 transition-colors group"
                    onClick={() => {
                      onSelect(journal.problemList);
                      setIsOpen(false);
                    }}
                  >
                    <span className="block text-[10px] text-gray-400 mb-0.5 group-hover:text-primary-400">
                      {formatDate(journal.createdAt as unknown)}
                    </span>
                    <span className="block text-xs text-gray-700 line-clamp-2 group-hover:text-primary-700">
                      {journal.problemList}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
